import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Users, PartyPopper, KeyRound, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { getNick, setNick, saveHostKey, normalizeCode } from "../lib/party";
import { BreadcrumbJsonLd } from "../components/JsonLd";

const field = "mt-1 w-full bg-[color:var(--bg-elev)] border border-[color:var(--border)] rounded-lg px-3 py-2.5 text-[color:var(--text)] focus:outline-none focus:border-[color:var(--accent)] transition-colors";

export default function Party() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [channels, setChannels] = useState([]);
  const [nick, setNickState] = useState(getNick());
  const [channelId, setChannelId] = useState(params.get("channel") || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/channels").then(r => setChannels(r.data)).catch(() => {});
  }, []);

  const requireNick = () => {
    if (!nick.trim()) { toast.error("Choisissez un pseudo"); return false; }
    setNick(nick);
    return true;
  };

  const create = async (e) => {
    e.preventDefault();
    if (!requireNick() || !channelId) return;
    setBusy("create");
    try {
      const { data } = await api.post("/party", { channel_id: channelId, nick: nick.trim() });
      saveHostKey(data.code, data.host_key);
      toast.success(`Salle ${data.code} ouverte`);
      nav(`/party/${data.code}`);
    } catch (err) {
      toast.error(apiError(err, "Impossible de créer la salle"));
      setBusy(null);
    }
  };

  const join = async (e) => {
    e.preventDefault();
    const c = normalizeCode(code);
    if (!requireNick() || c.length < 9) { if (c.length < 9) toast.error("Code incomplet"); return; }
    setBusy("join");
    try {
      await api.get(`/party/${c}`);
      nav(`/party/${c}`);
    } catch (err) {
      toast.error(apiError(err, "Salle introuvable"));
      setBusy(null);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-12" data-testid="party-lobby">
      <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Watch Party", path: "/party" }]} />
      <div className="mb-10 max-w-2xl">
        <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-3 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Regarder ensemble</div>
        <h1 className="font-display text-5xl sm:text-6xl">Watch Party</h1>
        <p className="text-[color:var(--text-muted)] mt-3">Ouvrez une salle sur une chaîne, partagez le code à votre crew : tout le monde voit la même vidéo au même moment, avec un chat en direct. Sans compte — juste un pseudo.</p>
      </div>

      <div className="max-w-md mb-8">
        <label className="block text-xs uppercase tracking-widest text-[color:var(--text-muted)]">Votre pseudo
          <input data-testid="party-nick" value={nick} onChange={e => setNickState(e.target.value)} maxLength={24} placeholder="ex. DJ Kwamé" className={field} />
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <form onSubmit={create} className="glass rounded-2xl p-7 space-y-4 border border-[color:var(--accent)]/30" data-testid="party-create-form">
          <div className="flex items-center gap-2"><PartyPopper className="w-5 h-5 text-[color:var(--accent)]" /><h2 className="font-display text-3xl">Ouvrir une salle</h2></div>
          <label className="block text-xs uppercase tracking-widest text-[color:var(--text-muted)]">Chaîne à diffuser
            <select data-testid="party-channel" required value={channelId} onChange={e => setChannelId(e.target.value)} className={field}>
              <option value="">— choisir une chaîne —</option>
              {channels.map(c => <option key={c.id} value={c.id}>{c.name} · {c.category}</option>)}
            </select>
          </label>
          <button data-testid="party-create-submit" disabled={busy === "create"} className="w-full inline-flex items-center justify-center gap-2 bg-[color:var(--accent)] text-black font-semibold px-5 py-3 rounded-full text-xs uppercase tracking-widest disabled:opacity-50 hover:brightness-110 transition">
            {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PartyPopper className="w-4 h-4" />} Créer et obtenir le code
          </button>
          <p className="text-[10px] text-[color:var(--text-muted)]">Vous serez l'hôte : vous pourrez passer à la vidéo suivante pour tout le monde. La salle expire après 24h sans activité.</p>
        </form>

        <form onSubmit={join} className="glass rounded-2xl p-7 space-y-4" data-testid="party-join-form">
          <div className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-[color:var(--accent)]" /><h2 className="font-display text-3xl">Rejoindre</h2></div>
          <label className="block text-xs uppercase tracking-widest text-[color:var(--text-muted)]">Code de la salle
            <input data-testid="party-code" value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="FEDE-7K2P" maxLength={9} className={`${field} font-mono tracking-[0.3em] text-lg uppercase`} />
          </label>
          <button data-testid="party-join-submit" disabled={busy === "join"} className="w-full inline-flex items-center justify-center gap-2 border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] px-5 py-3 rounded-full text-xs uppercase tracking-widest disabled:opacity-50 transition-colors">
            {busy === "join" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Entrer dans la salle
          </button>
          <p className="text-[10px] text-[color:var(--text-muted)]">Un ami vous a envoyé un lien <span className="font-mono">/party/FEDE-…</span> ? Ouvrez-le directement, le code est déjà rempli.</p>
        </form>
      </div>
      <Link to="/channels" className="inline-block mt-8 text-xs uppercase tracking-widest text-[color:var(--text-muted)] hover:text-white transition-colors">← Voir les chaînes</Link>
    </div>
  );
}
